// Recurring Invoice — recurring-invoice-module.html and "Recurring Invoice 2.txt".
//
// One module with its own rail: Dashboard · Recurring Profiles · New Profile ·
// Generated Invoices · Profile Report · Invoice Report · Schedule & Email.
// Each screen has its own URL (/Transactions/RecurringInvoice/<Screen>[/guid]),
// so the server menu can link straight to any of them.
//
// The API is new — every endpoint and field is in
// docs/BACKEND-COMPLETE.md (Part 5.6). Until it ships the screens load empty
// and say so; nothing on them is invented.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import toast from "react-hot-toast";
import { fetchProduct } from "../../redux/userSetting";
import { fetchUserByBranch } from "../../redux/userServices";
import { fetchBranchSettings } from "../../redux/settingbyBranch";
import { getCustomersList, getTaxList, getWareHouseList } from "../../services/MasterApiServices";
import { getcashorbank } from "../../services/AccountApiServices";
import {
  cancelRecurringInvoice,
  deleteRecurringProfile,
  emailRecurringInvoice,
  generateRecurringInvoiceNow,
  getNextProfileNo,
  getRecurringInvoices,
  getRecurringProfiles,
  getRecurringSettings,
  receiveRecurringPayment,
  runRecurringScheduler,
  saveRecurringProfile,
  saveRecurringSettings,
  setRecurringProfileStatus,
  updateRecurringInvoice,
} from "../../services/RecurringInvoiceApiServices";
import CustomerModal from "../../components/master/modal/CustomerModal";
import { IC } from "../../components/transactions/recurring/RecurringParts";
import RecurringDashboard from "../../components/transactions/recurring/RecurringDashboard";
import { BLANK_PF, ProfileList, ProfileReport } from "../../components/transactions/recurring/ProfileScreens";
import { BLANK_INF, InvoiceList, InvoiceReport, defaultInvoiceFrom } from "../../components/transactions/recurring/InvoiceScreens";
import ProfileForm from "../../components/transactions/recurring/ProfileForm";
import ProfileView from "../../components/transactions/recurring/ProfileView";
import { InvoiceEditDrawer, InvoiceViewDrawer, ReceiveDrawer } from "../../components/transactions/recurring/InvoiceDrawers";
import RecurringSettings, { toSettings } from "../../components/transactions/recurring/RecurringSettings";
import { printInvoicePdf } from "../../components/transactions/recurring/recurringExport";
import {
  blankLine,
  blankProfile,
  fmtD,
  iso,
  lineOf,
  toInvoice,
  toProfile,
  toProfilePayload,
  today,
} from "../../components/transactions/recurring/recurringModel";

const MySwal = withReactContent(Swal);
const BASE = "/Transactions/RecurringInvoice";

const SCREENS = {
  dashboard: "dash",
  profiles: "profiles",
  new: "form",
  edit: "form",
  clone: "form",
  view: "view",
  invoices: "invoices",
  profilereport: "rptProfile",
  invoicereport: "rptInvoice",
  settings: "settings",
};

// The screens are reached from the sidebar: Transactions > Recurring Invoice
// (menu rows from DB update script section 28). Their rights are looked up by
// these names.
const RECURRING_MENU_NAMES = [
  "Recurring Dashboard",
  "Recurring Profiles",
  "New Profile",
  "Generated Invoices",
  "Profile Report",
  "Invoice Report",
  "Schedule & Email",
];

const errMsg = (e, fallback) => e?.response?.data?.Message || e?.response?.data?.message || fallback;
const asList = (d) => (Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : []);

const RecurringInvoice = () => {
  const { screen: slug = "", guid = "" } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { branch } = useSelector((state) => state.branchlist);
  const { userId } = useSelector((state) => state.userauth);
  const { menuItems } = useSelector((state) => state.amc);
  const productList = useSelector((state) => state.usersetting?.productList);
  const UserListByBranch = useSelector((state) => state.userservice?.UserListByBranch);
  const branchsettings = useSelector((state) => state.settings?.branchsettings);
  const companyName = useSelector((state) => state.companyInfo?.companyInfolist?.name || state.companyInfo?.companyInfolist?.[0]?.name);

  const key = slug.toLowerCase();
  const screen = SCREENS[key] || "dash";
  const formMode = key === "edit" ? "edit" : key === "clone" ? "clone" : "new";
  const go = useCallback((to) => navigate(BASE + "/" + to), [navigate]);

  // The module's screens are the items of the Transactions > Recurring Invoice
  // menu group; a right on any of them counts for the module. A database
  // without those menu rows yet keeps full access, so the screen still works.
  const permit = useMemo(() => {
    const rows = (menuItems?.menuPermissions || []).filter?.((e) => RECURRING_MENU_NAMES.includes(e.menu?.name)) || [];
    if (!rows.length) return { view: true, add: true, edit: true, deletion: true };
    return ["view", "add", "edit", "deletion"].reduce((p, k) => ({ ...p, [k]: rows.some((r) => r[k]) }), {});
  }, [menuItems]);

  const cur = branchsettings?.currency?.code || branchsettings?.currency?.symbol || "";

  /* ---------------------------------------------------------------- data */
  const [profiles, setProfiles] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invFrom, setInvFrom] = useState(defaultInvoiceFrom);
  const [apiMissing, setApiMissing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(() => toSettings(null));
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadProfiles = useCallback(async () => {
    if (!branch?.guid) return [];
    try {
      const rows = asList(await getRecurringProfiles(branch.guid)).map(toProfile);
      setProfiles(rows);
      setApiMissing(false);
      return rows;
    } catch (e) {
      console.error("Error loading recurring profiles:", e);
      setProfiles([]);
      setApiMissing(true);
      return [];
    }
  }, [branch]);

  const loadInvoices = useCallback(async () => {
    if (!branch?.guid) return;
    try {
      setInvoices(asList(await getRecurringInvoices(branch.guid, invFrom, "")).map(toInvoice));
    } catch (e) {
      console.error("Error loading recurring invoices:", e);
      setInvoices([]);
    }
  }, [branch, invFrom]);

  const reload = useCallback(async () => {
    await Promise.all([loadProfiles(), loadInvoices()]);
  }, [loadProfiles, loadInvoices]);

  useEffect(() => {
    if (!branch?.guid) return;
    setLoading(true);
    Promise.all([loadProfiles(), loadInvoices()]).finally(() => setLoading(false));
  }, [loadProfiles, loadInvoices, branch]);

  useEffect(() => {
    if (!branch?.guid) return;
    dispatch(fetchProduct(branch));
    dispatch(fetchUserByBranch(branch.guid));
    dispatch(fetchBranchSettings(branch.guid));
    getCustomersList(branch)
      .then((d) => setCustomers(asList(d).filter((c) => c && c.guid && !c.isDeleted).sort((a, b) => String(a.name).localeCompare(String(b.name)))))
      .catch(() => setCustomers([]));
    getWareHouseList()
      .then((d) => setWarehouses(asList(d).filter((w) => !w.branchID || w.branchID === branch.guid)))
      .catch(() => setWarehouses([]));
    getTaxList()
      .then((d) => setTaxes(asList(d)))
      .catch(() => setTaxes([]));
    getcashorbank()
      .then((r) => setLedgers(asList(r?.data ?? r).map((l) => ({ value: l.guid, label: l.name }))))
      .catch(() => setLedgers([]));
    getRecurringSettings(branch.guid)
      .then((s) => {
        setSettings(toSettings(s));
        setSettingsLoaded(!!s);
      })
      .catch(() => setSettingsLoaded(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch]);

  const lookups = useMemo(
    () => ({
      customers,
      warehouses,
      taxes,
      products: (Array.isArray(productList) ? productList : []).filter((p) => p && p.guid),
      salespeople: (UserListByBranch || []).filter((u) => u && u.guid).map((u) => ({ guid: u.guid, name: u.name })),
    }),
    [customers, warehouses, taxes, productList, UserListByBranch]
  );

  // the invoice listing and report can reach further back than the default window
  const widenInvoices = useCallback((from) => {
    setInvFrom((cur0) => (from && from < cur0 ? from : cur0));
  }, []);

  /* ---------------------------------------------------------------- filters (kept across screens) */
  const [pf, setPf] = useState(BLANK_PF);
  const [inf, setInf] = useState(BLANK_INF);

  /* ---------------------------------------------------------------- form record */
  const [formRecord, setFormRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [custModal, setCustModal] = useState(false);
  const profileByGuid = useCallback((g) => profiles.find((p) => p.guid === g), [profiles]);
  // edit / clone wait for their source row; a new form never re-opens because the list reloaded
  const sourceFound = formMode !== "new" && !!profileByGuid(guid);

  useEffect(() => {
    if (screen !== "form") return;
    let live = true;
    const src = guid ? profileByGuid(guid) : null;
    if (formMode !== "new" && !src) {
      setFormRecord(null);
      return;
    }
    const defaultWh = branchsettings?.defaultWareHouseID || "";
    if (formMode === "edit") {
      setFormRecord({ ...src, items: src.items.map((l) => ({ ...l })) });
      return;
    }
    const base =
      formMode === "clone"
        ? {
            ...src,
            guid: "",
            no: "",
            status: "Active",
            failed: false,
            date: iso(today()),
            items: src.items.map((l) => ({ ...l, guid: "", key: blankLine().key })),
          }
        : blankProfile({ whGuid: defaultWh });
    // ask for the next number before the form opens, so a late answer cannot
    // replace the record under someone who is already typing
    setFormRecord(null);
    getNextProfileNo(branch?.guid)
      .then((n) => {
        const no = typeof n === "string" ? n : n?.profileNo || n?.no;
        return no ? { ...base, no } : base;
      })
      .catch(() => base)
      .then((rec) => {
        if (live) setFormRecord(rec);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, formMode, guid, sourceFound]);

  const saveProfile = async (draft) => {
    setSaving(true);
    try {
      const res = await saveRecurringProfile(toProfilePayload(draft, branch, userId));
      if (res?.data === false) throw new Error("not saved");
      const saved = res?.data && typeof res.data === "object" ? toProfile(res.data) : null;
      toast.success(
        (saved?.no || draft.no || "Profile") + (draft.guid ? " saved" : " created") + (draft.status === "Active" && draft.next ? " — next invoice " + fmtD(saved?.next || draft.next) : "")
      );
      await loadProfiles();
      if (saved?.guid) go("View/" + saved.guid);
      else if (draft.guid) go("View/" + draft.guid);
      else go("Profiles");
    } catch (e) {
      console.error("Error saving recurring profile:", e);
      toast.error(errMsg(e, apiMissing ? "The recurring-invoice API is not on this server yet" : "Could not save the profile"));
    } finally {
      setSaving(false);
    }
  };

  /* ---------------------------------------------------------------- profile actions */
  const confirm = (title, text, yes) =>
    MySwal.fire({
      title,
      text,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      cancelButtonColor: "#ff0000",
      confirmButtonText: yes,
      cancelButtonText: "Cancel",
    }).then((r) => r.isConfirmed);

  const setStatus = async (p, status, okMsg) => {
    setBusy(true);
    try {
      await setRecurringProfileStatus({ guid: p.guid, status, updatedUser: `{${userId?.id}}` });
      toast.success(okMsg);
      await loadProfiles();
    } catch (e) {
      toast.error(errMsg(e, "Could not change the status"));
    } finally {
      setBusy(false);
    }
  };

  const makeNow = async (p) => {
    if (!(await confirm("Create the invoice now?", p.no + " · " + p.custName + " · " + p.net.toFixed(2) + ". The next cycle moves on from it.", "Yes, create it"))) return;
    setBusy(true);
    try {
      const inv = await generateRecurringInvoiceNow({ profileGuid: p.guid, branch: { guid: branch?.guid }, updatedUser: `{${userId?.id}}` });
      toast.success("Invoice " + (inv?.invoiceNo || "") + " raised");
      await reload();
    } catch (e) {
      toast.error(errMsg(e, "Could not raise the invoice"));
    } finally {
      setBusy(false);
    }
  };

  const profileAct = {
    create: () => go("New"),
    view: (p) => go("View/" + p.guid),
    edit: (p) => go("Edit/" + p.guid),
    clone: (p) => go("Clone/" + p.guid),
    back: () => go("Profiles"),
    del: async (p) => {
      if (!(await confirm("Delete " + (p.no || "this profile") + "?", "Invoices already raised stay. You won't be able to revert this!", "Yes, delete"))) return;
      try {
        await deleteRecurringProfile({ guid: p.guid, updatedUser: `{${userId?.id}}` });
        toast.success((p.no || "Profile") + " deleted");
        await loadProfiles();
        if (screen === "view") go("Profiles");
      } catch (e) {
        toast.error(errMsg(e, "Could not delete the profile"));
      }
    },
    pause: (p) => setStatus(p, p.status === "Paused" ? "Active" : "Paused", (p.no || "Profile") + (p.status === "Paused" ? " resumed" : " paused")),
    cancel: async (p) => {
      if (!(await confirm("Cancel " + (p.no || "this profile") + "?", "It stays for the record but never bills again.", "Yes, cancel it"))) return;
      setStatus(p, "Cancelled", (p.no || "Profile") + " cancelled");
    },
    makeNow,
  };

  /* ---------------------------------------------------------------- invoice actions */
  const [drawer, setDrawer] = useState(null); // { kind: "view" | "receive" | "edit", x }
  const closeDrawer = useCallback(() => setDrawer((d) => (d ? { ...d, open: false } : d)), []);

  const invoiceAct = {
    view: (x) => setDrawer({ kind: "view", x, open: true }),
    receive: (x) => setDrawer({ kind: "receive", x, open: true }),
    edit: (x) => setDrawer({ kind: "edit", x, open: true }),
    viewInvoice: (x) => setDrawer({ kind: "view", x, open: true }),
    print: (x) => printInvoicePdf(x, companyName, cur),
    email: async (x) => {
      try {
        await emailRecurringInvoice({ guid: x.guid, updatedUser: `{${userId?.id}}` });
        toast.success("Emailed " + x.no + (x.custEmail ? " to " + x.custEmail : ""));
      } catch (e) {
        toast.error(errMsg(e, "Could not email the invoice"));
      }
    },
    cancel: async (x) => {
      if (!(await confirm("Cancel " + x.no + "?", "The customer will no longer owe this cycle.", "Yes, cancel it"))) return;
      try {
        await cancelRecurringInvoice({ guid: x.guid, updatedUser: `{${userId?.id}}` });
        toast.success(x.no + " cancelled");
        await loadInvoices();
      } catch (e) {
        toast.error(errMsg(e, "Could not cancel the invoice"));
      }
    },
  };

  const doReceive = async (x, f) => {
    setBusy(true);
    try {
      const r = await receiveRecurringPayment({
        invoiceGuid: x.guid,
        receiptDate: f.date,
        amount: f.amount,
        paymentMode: f.mode,
        reference: f.ref,
        depositLedger: { guid: f.ledger },
        branch: { guid: branch?.guid },
        updatedUser: `{${userId?.id}}`,
      });
      closeDrawer();
      toast.success("Payment received · " + x.no + (r?.salesInvoiceNo ? " posted as " + r.salesInvoiceNo : " posted as a tax invoice"));
      await loadInvoices();
    } catch (e) {
      toast.error(errMsg(e, "Could not post the payment"));
    } finally {
      setBusy(false);
    }
  };

  const doEditInvoice = async (x, f, t) => {
    setBusy(true);
    try {
      await updateRecurringInvoice({
        guid: x.guid,
        invoiceDate: f.date,
        dueDate: f.due,
        details: f.items.map((l) => {
          const z = lineOf(l);
          return {
            guid: l.guid,
            product: { guid: l.productGuid },
            note: l.note,
            quantity: +l.qty || 0,
            rate: +l.rate || 0,
            discPercentage: +l.disc || 0,
            discRate: z.discRate,
            isTaxable: !!l.taxable,
            taxPercentage: l.taxable ? +l.vatPct || 0 : 0,
            taxAmount: z.vat,
            netAmount: z.net,
          };
        }),
        taxableTotal: t.taxable,
        vatTotal: t.vat,
        netTotal: t.net,
        updatedUser: `{${userId?.id}}`,
      });
      closeDrawer();
      toast.success(x.no + " updated");
      await loadInvoices();
    } catch (e) {
      toast.error(errMsg(e, "Could not update the invoice"));
    } finally {
      setBusy(false);
    }
  };

  /* ---------------------------------------------------------------- dashboard hooks */
  const onTile = (k) => {
    if (k === "pending" || k === "paid") {
      setInf({ ...BLANK_INF, pay: k === "paid" ? "Paid" : "Pending" });
      return go("Invoices");
    }
    if (k === "genMonth") {
      setInf({ ...BLANK_INF, pay: "", from: iso(new Date(today().getFullYear(), today().getMonth(), 1)), to: iso(today()) });
      return go("Invoices");
    }
    if (k === "cancelled") setPf({ ...BLANK_PF, status: "Cancelled" });
    else if (k === "expiring") setPf({ ...BLANK_PF, status: "", counter: "expiring" });
    else setPf({ ...BLANK_PF, counter: k });
    return go("Profiles");
  };
  const onQueue = (k) => {
    if (k === "overdue") {
      setInf({ ...BLANK_INF, counter: "overdue" });
      return go("Invoices");
    }
    if (k === "pending") {
      setInf(BLANK_INF);
      return go("Invoices");
    }
    if (k === "paused") setPf({ ...BLANK_PF, status: "Paused" });
    else if (k === "expiring") setPf({ ...BLANK_PF, status: "", counter: "expiring" });
    else setPf({ ...BLANK_PF, counter: k });
    return go("Profiles");
  };
  const runNow = async () => {
    setBusy(true);
    try {
      const r = await runRecurringScheduler({ branch: { guid: branch?.guid }, updatedUser: `{${userId?.id}}` });
      const raised = r?.raised ?? r?.Raised;
      toast.success(raised ? raised + " invoices raised by the scheduler" : "Nothing was due — nothing raised");
      if (r?.failed) toast.error(r.failed + " profiles failed — see Needs attention");
      await reload();
    } catch (e) {
      toast.error(errMsg(e, "Could not run the scheduler"));
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async (s) => {
    setSaving(true);
    try {
      await saveRecurringSettings({ ...s, branch: { guid: branch?.guid }, updatedUser: `{${userId?.id}}` });
      setSettings(s);
      setSettingsLoaded(true);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(errMsg(e, "Could not save the settings"));
    } finally {
      setSaving(false);
    }
  };

  /* ---------------------------------------------------------------- render */
  const viewing = screen === "view" ? profileByGuid(guid) : null;
  const dx = drawer?.x;

  let body;
  if (!permit.view) {
    body = <div className="axrc-empty">You do not have permission to view recurring invoices.</div>;
  } else if (screen === "dash") {
    body = (
      <RecurringDashboard
        profiles={profiles}
        invoices={invoices}
        cur={cur}
        branchName={branch?.name}
        permit={permit}
        running={busy}
        onTile={onTile}
        onQueue={onQueue}
        onCustomer={(g) => {
          setPf({ ...BLANK_PF, cust: g });
          go("Profiles");
        }}
        onRunNow={runNow}
        onNew={() => go("New")}
      />
    );
  } else if (screen === "profiles") {
    body = (
      <ProfileList
        profiles={profiles}
        invoices={invoices}
        pf={pf}
        setPf={setPf}
        permit={permit}
        act={profileAct}
        onReload={reload}
        onGoInvoices={() => onTile("genMonth")}
      />
    );
  } else if (screen === "form") {
    body = formRecord ? (
      <ProfileForm
        record={formRecord}
        mode={formMode}
        lookups={lookups}
        permit={permit}
        saving={saving}
        onSave={saveProfile}
        onCancel={() => (guid ? go("View/" + guid) : go("Profiles"))}
        onNewCustomer={() => setCustModal(true)}
      />
    ) : (
      <div className="axrc-empty">{loading || formMode === "new" ? "Loading…" : "That profile was not found in this branch."}</div>
    );
  } else if (screen === "view") {
    body = viewing ? (
      <ProfileView
        key={viewing.guid}
        p={viewing}
        invoices={invoices}
        permit={permit}
        cur={cur}
        busy={busy}
        act={{ ...profileAct, viewInvoice: invoiceAct.view, receive: invoiceAct.receive }}
      />
    ) : (
      <div className="axrc-empty">{loading ? "Loading the profile…" : "That profile was not found in this branch."}</div>
    );
  } else if (screen === "invoices") {
    body = <InvoiceList invoices={invoices} inf={inf} setInf={setInf} permit={permit} act={invoiceAct} onReload={reload} cur={cur} onRange={widenInvoices} />;
  } else if (screen === "rptProfile") {
    body = <ProfileReport profiles={profiles} permit={permit} act={profileAct} cur={cur} />;
  } else if (screen === "rptInvoice") {
    body = <InvoiceReport invoices={invoices} permit={permit} act={invoiceAct} cur={cur} onRange={widenInvoices} />;
  } else {
    body = <RecurringSettings settings={settings} loaded={settingsLoaded} onSave={saveSettings} permit={permit} saving={saving} />;
  }

  return (
    <div className="axrc-page">
      <div className="page-wrapper">
        <div className="content">
          {/* the module's screens are in the sidebar (Transactions > Recurring Invoice) */}
          <div className="axrc-shell axrc-shell-full">
            <div className="axrc-screenwrap">
              {apiMissing && (
                <div className="axrc-banner" role="status">
                  {IC.warn}
                  <span>
                    The recurring-invoice API is not on this server yet, so these screens are empty and saving will fail. The backend
                    contract is in <b>docs/BACKEND-COMPLETE.md (Part 5.6)</b>.
                  </span>
                </div>
              )}
              {body}
            </div>
          </div>
        </div>
      </div>

      {dx && drawer.kind === "view" && (
        <InvoiceViewDrawer
          x={dx}
          open={drawer.open}
          onClose={closeDrawer}
          permit={permit}
          onPrint={invoiceAct.print}
          onReceive={(x) => {
            closeDrawer();
            setTimeout(() => setDrawer({ kind: "receive", x, open: true }), 230);
          }}
        />
      )}
      {dx && drawer.kind === "receive" && (
        <ReceiveDrawer
          x={dx}
          open={drawer.open}
          onClose={closeDrawer}
          ledgers={ledgers}
          busy={busy}
          nextDue={(profileByGuid(dx.profileGuid) || {}).next}
          onConfirm={doReceive}
        />
      )}
      {dx && drawer.kind === "edit" && <InvoiceEditDrawer x={dx} open={drawer.open} onClose={closeDrawer} busy={busy} onSave={doEditInvoice} />}

      {custModal && (
        <CustomerModal
          mode="add"
          data={{}}
          handleClose={() => setCustModal(false)}
          handleRefresh={() =>
            getCustomersList(branch)
              .then((d) => setCustomers(asList(d).filter((c) => c && c.guid && !c.isDeleted).sort((a, b) => String(a.name).localeCompare(String(b.name)))))
              .catch(() => {})
          }
        />
      )}
    </div>
  );
};

export default RecurringInvoice;
